import { useEffect, useState } from 'react'
import { Ticket } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { SettingsCrumb } from '../admin/SettingsCrumb.js'
import { adminMutate, apiGet, Select, usePermissions } from '../admin/primitives.js'
import { Field, NumberInput, SwitchRow, TextInput } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { Link } from '../router.js'
import { lireParts, type PartsAssociations } from '../finance/PartAssociation.js'
import { ARattacher } from './Rattachements.js'
import { NuitsDesEvenements } from './NuitsEvenementsAssociations.js'
import { casDuLien, idDuCas } from './rattachement-model.js'
import {
  ALERTE_HEURES_MAX, ALERTE_HEURES_MIN, COULEURS, DEFAUT, HORIZON_MAX, HORIZON_MIN, JOURS_MAX, JOURS_MIN, PART_CLUB_MAX, PART_CLUB_MIN,
  PATIENCE_HEURES_MAX, PATIENCE_HEURES_MIN, RAPPEL_HEURES_MAX, TAUX_MAX, TAUX_MIN,
  avisTuileFixee, introGraphique, invalide, memes, resumeAlerte,
  type ChoixFixesParAppareil, type CouleurBilletterie, type Reglages,
} from './billetteries-reglages-model.js'

/**
 * /settings/soirees/billetteries — RÉGLAGES SOIRÉES → BILLETTERIES (16/10/2026).
 *
 * Deux billetteries vendent l'entrée des nuits : Shotgun (le club) et Pretix
 * (les soirées d'associations). Cette page règle ce que le direct en montre,
 * la place des soirées d'associations dans les analyses et le rattachement à
 * la soirée du club. « À rattacher » et la nuit de chaque événement
 * d'association (corrigeable à la main quand la date saisie dans Pretix
 * trompe : ESN, annoncée le 30/04 à 00 h) ont déménagé le 20/10/2026 dans
 * Exploitation › Associations › À décider ; un lien y mène, en bas.
 *
 * Les réglages passent par la Save Bar.
 */

export function BilletteriesSettingsPage() {
  const meta = useShellMeta()
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const litAssociations = permissions.permissions.includes('association.read')
  // La part des associations est une règle de la Comptabilité : ses droits à elle.
  const peutLireParts = permissions.permissions.includes('finance.reference.read')
  const [parts, setParts] = useState<PartsAssociations | null>(null)
  const [serveur, setServeur] = useState<Reglages | null>(null)
  const [r, setR] = useState<Reglages>(DEFAUT)
  const [fixes, setFixes] = useState<ChoixFixesParAppareil | null>(null)

  const charger = () => {
    void apiGet<{ billetteries?: Reglages; analytics_tile_fixed?: ChoixFixesParAppareil }>('/api/v1/soirees/settings').then((c) => {
      const b = { ...DEFAUT, ...(c.billetteries ?? {}) }
      setServeur(b); setR(b); setFixes(c.analytics_tile_fixed ?? null)
    }).catch(() => setServeur(null))
  }
  const chargerParts = () => {
    void apiGet<Parameters<typeof lireParts>[0]>('/api/v1/finance/associations/shares')
      .then((j) => setParts(lireParts(j))).catch(() => setParts(null))
  }
  useEffect(() => { charger() }, [])
  useEffect(() => { if (peutLireParts) chargerParts() }, [peutLireParts]) // eslint-disable-line react-hooks/exhaustive-deps
  // Arrivée par l'alerte « avant la nuit », sans « Consulter les associations »
  // (`?cas=pretix|2026-10-15`, redirigé depuis Associations › À décider) : la
  // page défile jusqu'au cas, une fois sa carte affichée (la liste se lit
  // après la page : 3 secondes au plus, sinon le bloc « À rattacher »).
  const [cas] = useState(() => casDuLien(window.location.search))
  useEffect(() => {
    if (!cas || permissions.loading || litAssociations) return
    let essais = 0
    let t = 0
    const viser = () => {
      const carte = document.getElementById(idDuCas(cas))
      if (!carte && essais++ < 12) { t = window.setTimeout(viser, 250); return }
      ;(carte ?? document.getElementById('bil-decisions'))?.scrollIntoView({ block: carte ? 'center' : 'start', behavior: 'smooth' })
      carte?.classList.add('so-ratt--vise')
    }
    t = window.setTimeout(viser, 120)
    return () => window.clearTimeout(t)
  }, [cas, permissions.loading, litAssociations])

  useSaveBar({
    dirty: serveur !== null && peutGerer && !memes(r, serveur),
    saveLabel: 'Enregistrer les billetteries',
    invalid: invalide(r),
    onSave: async () => {
      const x = await adminMutate<{ ok?: boolean; error?: string }>('PUT', '/api/v1/soirees/settings', { billetteries: { ...r, pretix_label: r.pretix_label.trim(), undecided_alert_time: r.undecided_alert_time.trim() } })
      if (x.status !== 200) return 'Enregistrement impossible.'
      charger()
      // Le regroupement change la liste des soirées : la nuit retenue, non.
      return true
    },
    onCancel: () => { if (serveur) setR(serveur) },
    successMessage: 'Billetteries enregistrées.',
  })

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger">
        <SettingsCrumb />
        <h1 className="lc-title"><Ticket size={26} aria-hidden="true" /> Billetteries</h1>
        <p className="lc-subtitle">
          Shotgun pour les soirées du club, Pretix pour celles des associations : ce que le direct en montre,
          leur place dans les analyses et leur rattachement à la soirée du club.
        </p>

        <section className="lc-panel gd-panel" aria-labelledby="bil-direct">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="bil-direct">Le direct</h2></header>
          <div className="lc-panel__body">
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Quand une soirée d’association a lieu la même nuit qu’une soirée du club, la porte voit les deux :
              « 636 entrées cette nuit : L’ÉTUDIANTE 421 (Shotgun) · MEDECINE 215 (Pretix) ». Ce sont des billets
              scannés, jamais des personnes présentes.
            </p>
            <Field label="Le grand nombre du direct" helper="Sur la carte de la soirée en cours et sur le tableau de bord.">
              <Select
                value={r.live_count}
                onChange={(v) => setR({ ...r, live_count: v === 'SOIREE' ? 'SOIREE' : 'NIGHT' })}
                ariaLabel="Le grand nombre du direct"
                options={[
                  { value: 'NIGHT', label: 'Le total de la nuit, toutes billetteries (conseillé)' },
                  { value: 'SOIREE', label: 'La soirée seule, la nuit affichée en dessous' },
                ]}
              />
            </Field>
            <SwitchRow checked={r.show_dashboard} onChange={(v) => setR({ ...r, show_dashboard: v })} disabled={!peutGerer}
              title="Sur le tableau de bord"
              desc="La carte « Soirée en cours » montre la nuit et ses lignes par billetterie." />
            <SwitchRow checked={r.show_cockpit} onChange={(v) => setR({ ...r, show_cockpit: v })} disabled={!peutGerer}
              title="Dans la fiche d’une soirée"
              desc="Sous les billets scannés de la soirée, la nuit entière — chaque ligne ouvre la soirée correspondante." />
            <SwitchRow checked={r.group_per_night} onChange={(v) => setR({ ...r, group_per_night: v })} disabled={!peutGerer}
              title="Regrouper les événements d’une même nuit"
              desc="Le 09/09, les huit événements INTERSANTE qui ont vendu font une seule ligne « INTERSANTE (×8) », dans le direct comme dans la liste des soirées (le neuvième, sans aucune vente, n’est pas suivi par défaut)." />
            <Field label="Libellé des entrées d’associations" helper="Utilisé quand des événements regroupés n’ont pas de nom commun ; la billetterie entre parenthèses n’est pas répétée (« Entrées BDE (×2) »).">
              <TextInput value={r.pretix_label} onChange={(v) => setR({ ...r, pretix_label: v })} maxLength={60} ariaLabel="Libellé des entrées d’associations" disabled={!peutGerer} />
            </Field>
          </div>
        </section>

        <section className="lc-panel gd-panel" aria-labelledby="bil-rattachement">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="bil-rattachement">Rattachement à la soirée du club</h2></header>
          <div className="lc-panel__body">
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Un événement d’association de la même nuit qu’une soirée du club ne fait plus qu’une ligne avec elle :
              « L’ÉTUDIANTE · + MEDECINE (Pretix) ». Ses billets ne bougent pas, la Comptabilité non plus ; tout se
              défait d’un clic. Gestion rattache seul quand une seule soirée du club a lieu cette nuit-là, que les horaires
              se recouvrent à moitié au moins, et que 90 % des entrées ont été scannées pendant elle (à une heure près).
              Même nom : « certain » ; sinon : « probable ».
            </p>
            <Field label="Rattacher automatiquement" helper="Ce que Gestion ne rattache pas seul arrive dans Exploitation › Associations › À décider, et sur la page Soirées.">
              <Select
                value={r.attach_mode}
                onChange={(v) => setR({ ...r, attach_mode: v === 'CERTAIN_ONLY' ? 'CERTAIN_ONLY' : v === 'NEVER' ? 'NEVER' : 'CERTAIN_AND_PROBABLE' })}
                ariaLabel="Rattacher automatiquement"
                options={[
                  { value: 'CERTAIN_AND_PROBABLE', label: 'Quand c’est certain ou probable (conseillé)' },
                  { value: 'CERTAIN_ONLY', label: 'Seulement quand c’est certain (même nom)' },
                  { value: 'NEVER', label: 'Jamais : Gestion me demande toujours' },
                ]}
              />
            </Field>
            <Field label="Dans les listes et le calendrier, un événement rattaché" helper="La fiche de la soirée montre toujours ses billetteries.">
              <Select
                value={r.attached_display}
                onChange={(v) => setR({ ...r, attached_display: v === 'INDENTED' ? 'INDENTED' : 'HIDDEN' })}
                ariaLabel="Événements rattachés dans les listes"
                options={[
                  { value: 'HIDDEN', label: 'Caché sous sa soirée (« + MEDECINE (Pretix) »)' },
                  { value: 'INDENTED', label: 'Montré en retrait, juste sous sa soirée' },
                ]}
              />
            </Field>
            <Field label="Le nombre d’une soirée dans les listes et le calendrier" helper="Ne change que le nombre de chaque ligne : la fiche de la soirée et le calendrier donnent toujours le total de toutes ses billetteries, et les prévisions restent celles de la soirée du club.">
              <Select
                value={r.soiree_count}
                onChange={(v) => setR({ ...r, soiree_count: v === 'SOIREE_ONLY' ? 'SOIREE_ONLY' : 'WITH_ATTACHED' })}
                ariaLabel="Le nombre d’une soirée"
                options={[
                  { value: 'WITH_ATTACHED', label: 'La soirée et ses événements rattachés (934 le 15/10)' },
                  { value: 'SOIREE_ONLY', label: 'La soirée seule (710 le 15/10)' },
                ]}
              />
            </Field>
            <SwitchRow checked={r.announce_auto_attach} onChange={(v) => setR({ ...r, announce_auto_attach: v })} disabled={!peutGerer}
              title="Annoncer chaque rattachement automatique"
              desc="Une notification dans le Centre d’activité, à ceux qui gèrent les soirées : « MEDECINE le 15/10 sera rattachée à L’ÉTUDIANTE », avec le chemin pour défaire." />
            <SwitchRow checked={r.announce_before_night} onChange={(v) => setR({ ...r, announce_before_night: v })} disabled={!peutGerer}
              title="Annoncer les rattachements avant la nuit"
              desc="Sur l’Accueil et dans le Centre d’activité, tant que la nuit n’est pas passée : « MEDECINE le 15/10 sera rattachée à L’ÉTUDIANTE », avec « Défaire » et « C’est la même soirée » à côté. Gestion rattache dès que l’événement paraît dans Pretix : une erreur se corrige avant la soirée, pas après. Seuls les rattachements faits par Gestion sont annoncés ; confirmer ou défaire retire l’annonce." />
            {r.announce_before_night ? (
              <Field label="À partir de" helper="Une nuit plus lointaine n’est pas annoncée : elle le sera le moment venu. La fiche de la soirée montre toujours ses rattachements.">
                <NumberInput value={r.announce_horizon_days} min={HORIZON_MIN} max={HORIZON_MAX} unit="jours avant la nuit" steppers
                  onChange={(v) => setR({ ...r, announce_horizon_days: Number(v) })} ariaLabel="Nombre de jours avant la nuit où le rattachement est annoncé" />
              </Field>
            ) : null}

            {/* L'ALERTE « À RATTACHER AVANT LA NUIT » (décision de l'exploitant, 20/10/2026). */}
            <h3 className="so-ratts__titre" id="bil-alerte">Prévenir avant la nuit</h3>
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Un événement que Gestion ne rattache pas seul compte comme une soirée à part — et devient « la soirée en cours »
              à sa propre ouverture (MEDECINE à 23 h 30, avant L’ÉTUDIANTE à 23 h 55, le 15/10). L’alerte part avant la nuit,
              dans Gestion et par e-mail (chacun choisit dans son Profil), et mène à Exploitation › Associations › À décider.
            </p>
            <Field label="Prévenir quand Gestion ne peut pas rattacher seul" helper={resumeAlerte(r)}>
              <Select
                value={r.undecided_alert}
                onChange={(v) => setR({ ...r, undecided_alert: v === 'HOURS_BEFORE' ? 'HOURS_BEFORE' : v === 'NEVER' ? 'NEVER' : 'DAY_BEFORE' })}
                ariaLabel="Quand prévenir"
                options={[
                  { value: 'DAY_BEFORE', label: 'La veille de la nuit, à une heure fixe (conseillé)' },
                  { value: 'HOURS_BEFORE', label: 'Quelques heures avant l’ouverture' },
                  { value: 'NEVER', label: 'Jamais' },
                ]}
              />
            </Field>
            {r.undecided_alert === 'DAY_BEFORE' ? (
              <Field label="À" helper="Heure du club. Un événement qui paraît plus tard (MEDECINE, le 14/10 à 16 h 05) est signalé dans les 5 minutes.">
                <input type="time" className="lc-input" value={r.undecided_alert_time} disabled={!peutGerer}
                  onChange={(e) => setR({ ...r, undecided_alert_time: e.target.value })} aria-label="Heure de l’alerte, la veille de la nuit" />
              </Field>
            ) : null}
            {r.undecided_alert === 'HOURS_BEFORE' ? (
              <Field label="Avant l’ouverture" helper="L’ouverture de la nuit : la plus tôt de l’événement et de la soirée du club proposée.">
                <NumberInput value={r.undecided_alert_hours} min={ALERTE_HEURES_MIN} max={ALERTE_HEURES_MAX} unit="heures" steppers
                  onChange={(v) => setR({ ...r, undecided_alert_hours: Number(v) })} ariaLabel="Nombre d’heures avant l’ouverture" />
              </Field>
            ) : null}
            {r.undecided_alert !== 'NEVER' ? (
              <>
                <SwitchRow checked={r.undecided_alert_no_club} onChange={(v) => setR({ ...r, undecided_alert_no_club: v })} disabled={!peutGerer}
                  title="Aussi quand aucune soirée du club n’existe encore"
                  desc="Une simple information : le 09/09, la soirée Shotgun INTERSANTE n’a paru que 5 minutes avant l’ouverture. Quand elle paraît, Gestion rattache seul si la règle et le réglage ci-dessus le permettent, sinon il demande ; le message ne propose jamais de garder l’événement séparé, ce qui empêcherait tout rattachement automatique." />
                <SwitchRow checked={r.undecided_alert_patience} onChange={(v) => setR({ ...r, undecided_alert_patience: v })} disabled={!peutGerer}
                  title="Attendre la première vente du club"
                  desc="Quand la seule raison est que la soirée du club n’a encore rien vendu : le 15/10, L’ÉTUDIANTE a vendu son premier billet à 6 h 18, et MEDECINE s’est rattachée seule. Pas de fausse urgence la veille." />
                {r.undecided_alert_patience ? (
                  <Field label="Au plus tard" helper="Sans première vente à cette heure-là, l’alerte part quand même.">
                    <NumberInput value={r.undecided_alert_patience_hours} min={PATIENCE_HEURES_MIN} max={PATIENCE_HEURES_MAX} unit="heures avant l’ouverture" steppers
                      onChange={(v) => setR({ ...r, undecided_alert_patience_hours: Number(v) })} ariaLabel="Attendre au plus tard, en heures avant l’ouverture" />
                  </Field>
                ) : null}
                <Field label="Rappel le jour même si rien n’est décidé" helper="0 : pas de rappel. Un seul message par cas et par étape, jamais plus.">
                  <NumberInput value={r.undecided_alert_reminder_hours} min={0} max={RAPPEL_HEURES_MAX} unit="heures avant l’ouverture" steppers
                    onChange={(v) => setR({ ...r, undecided_alert_reminder_hours: Number(v) })} ariaLabel="Rappel, en heures avant l’ouverture (0 = aucun)" />
                </Field>
              </>
            ) : null}
          </div>
        </section>

        <section className="lc-panel gd-panel" aria-labelledby="bil-formules">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="bil-formules">Billets des associations sur la fiche de la soirée</h2></header>
          <div className="lc-panel__body">
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Chaque billet Pretix comprend ses boissons : Essentiel (punch, bière, soft), Soft (2 softs ou un long drink),
              Prestige (2 long drinks). La formule est lue dans le nom du billet sur Pretix. Billets vendus, jamais le
              chiffre d’affaires.
            </p>
            <SwitchRow checked={r.show_bar_formules} onChange={(v) => setR({ ...r, show_bar_formules: v })} disabled={!peutGerer}
              title="Formules pour le bar"
              desc="Une tuile de la fiche, et une ligne sur la carte de la soirée en cours : combien d’Essentiel, de Soft et de Prestige sont vendus et déjà entrés (110 / 124 / 15 le 15/10). La tuile dit quand Pretix a été lu pour la dernière fois. Sur l’Accueil, la ligne suit aussi le réglage « tableau de bord »." />
            <SwitchRow checked={r.show_no_show} onChange={(v) => setR({ ...r, show_no_show: v })} disabled={!peutGerer}
              title="Venue par formule et par association"
              desc="Billets vendus face aux entrées. La nuit finie, et Pretix relu en entier après elle, la part de billets payés jamais scannés : 10 % pour MEDECINE, 12,7 % sur l’Essentiel. Tant que la lecture est incomplète, aucun taux n’est affiché. Une tuile de la fiche, et la ligne de chaque association rattachée." />
            <SwitchRow checked={r.association_label} onChange={(v) => setR({ ...r, association_label: v })} disabled={!peutGerer}
              title="Étiquette « Soirée association »"
              desc="Quand Shotgun ne vend presque rien et que les associations portent la nuit (09/09 : 0 billet Shotgun, 979 Pretix), la fiche le dit et retire les prévisions : elles ne lisent que Shotgun et montreraient « 0 face à l’an dernier ». Jamais avant la nuit : une soirée qui vend le jour même et à la porte (15/10 : 1 billet Shotgun à 16 h, 711 à la fin) garderait sinon sa prévision cachée. Pendant la nuit, seulement sans aucun billet Shotgun." />
            {r.association_label ? (
              <Field label="« Presque rien » : Shotgun pèse au plus" helper="La part des billets de la nuit vendus sur Shotgun, une fois la nuit finie. À 0 %, seulement une soirée Shotgun sans aucun billet.">
                <NumberInput value={r.association_max_pct} min={PART_CLUB_MIN} max={PART_CLUB_MAX} unit="%" steppers
                  onChange={(v) => setR({ ...r, association_max_pct: Number(v) })} ariaLabel="Part maximale de Shotgun pour une soirée association, en pourcentage" />
              </Field>
            ) : null}
            {/* La tuile Billetterie d'une soirée association (décision du 20/10/2026) : la fiche et la carte Sécurité. */}
            {r.association_label ? (
              <Field label="Sur une soirée association, la tuile Billetterie montre"
                helper="La même règle sur la carte « Soirée en cours » du hub Sécurité, que lit la porte. Ce sont des entrées scannées, jamais des personnes présentes. Si Shotgun vend un seul billet pendant la nuit, l’étiquette tombe et la tuile revient à Shotgun.">
                <Select
                  value={r.association_tile}
                  onChange={(v) => setR({ ...r, association_tile: v === 'CLUB' ? 'CLUB' : v === 'HIDDEN' ? 'HIDDEN' : 'NIGHT' })}
                  ariaLabel="Ce que montre la tuile Billetterie sur une soirée association"
                  options={[
                    { value: 'NIGHT', label: 'Toute la nuit, toutes billetteries (894 entrées le 09/09) — conseillé' },
                    { value: 'CLUB', label: 'La billetterie du club seule (0 le 09/09)' },
                    { value: 'HIDDEN', label: 'Masquer la tuile' },
                  ]}
                />
              </Field>
            ) : null}
            {r.association_label ? (
              <SwitchRow checked={r.association_hide_empty} onChange={(v) => setR({ ...r, association_hide_empty: v })} disabled={!peutGerer}
                title="Masquer les tuiles vides du club (Tarifs, Canaux)"
                desc="Sur une soirée association où Shotgun n’a vendu aucun billet, ces deux tuiles ne lisent que Shotgun et n’ont rien à montrer. Les formules Pretix (Essentiel, Soft, Prestige) restent dans « Formules pour le bar » et « Venue des associations »." />
            ) : null}
          </div>
        </section>

        <section className="lc-panel gd-panel" aria-labelledby="bil-graphique">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="bil-graphique">Le graphique de la soirée</h2></header>
          <div className="lc-panel__body">
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Le 15/10, le graphique de L’ÉTUDIANTE montrait 710 entrées ; la porte en avait vu 934, avec les 224 de
              MEDECINE. {introGraphique(r.chart_scope)}
            </p>
            {avisTuileFixee(fixes) ? <p className="lc-hint so-reglage-avis" role="note">{avisTuileFixee(fixes)}</p> : null}
            <Field label="Le graphique d’une soirée avec un événement rattaché"
              helper="La vue à l’ouverture. La tuile Analytics a un sélecteur pour en changer ; la Timeline suit toujours ce réglage.">
              <Select
                value={r.chart_scope}
                onChange={(v) => setR({ ...r, chart_scope: v === 'SOIREE' ? 'SOIREE' : 'NIGHT' })}
                ariaLabel="Le graphique d’une soirée avec un événement rattaché"
                options={[
                  { value: 'NIGHT', label: 'Toute la nuit : la soirée et ses événements rattachés (conseillé)' },
                  { value: 'SOIREE', label: 'La soirée seule' },
                ]}
              />
            </Field>
            <Field label="Sur toute la nuit, découper par" helper="« Par billetterie » empile Shotgun et Pretix : la hauteur de la pile est le total de la nuit.">
              <Select
                value={r.chart_breakdown}
                onChange={(v) => setR({ ...r, chart_breakdown: v === 'NONE' ? 'NONE' : 'BILLETTERIE' })}
                ariaLabel="Sur toute la nuit, découper par"
                options={[
                  { value: 'BILLETTERIE', label: 'Billetterie (conseillé)' },
                  { value: 'NONE', label: 'Rien : une seule courbe pour la nuit' },
                ]}
              />
            </Field>
            <Field label="Les ventes heure par heure quand elles ont duré moins de"
              helper="Au-delà, une barre par jour (coupée à minuit, heure de Paris). MEDECINE a tout vendu en 35 heures : au jour, cela faisait trois barres. Une échelle de temps fixée sur la tuile passe avant ce réglage.">
              <NumberInput value={r.chart_hourly_days} min={JOURS_MIN} max={JOURS_MAX} unit="jours" steppers
                onChange={(v) => setR({ ...r, chart_hourly_days: Number(v) })} ariaLabel="Nombre de jours de vente sous lequel le graphique passe à l’heure" />
            </Field>
            <div className="so-reglage-duo">
              <Field label="Couleur de Shotgun">
                <Select value={r.color_shotgun} options={[...COULEURS]} ariaLabel="Couleur de Shotgun"
                  onChange={(v) => setR({ ...r, color_shotgun: v as CouleurBilletterie })} />
              </Field>
              <Field label="Couleur de Pretix">
                <Select value={r.color_pretix} options={[...COULEURS]} ariaLabel="Couleur de Pretix"
                  onChange={(v) => setR({ ...r, color_pretix: v as CouleurBilletterie })} />
              </Field>
            </div>
            <p className="so-reglage-apercu" aria-hidden="true">
              <span><i style={{ background: `var(--${r.color_shotgun})` }} />Shotgun</span>
              <span><i style={{ background: `var(--${r.color_pretix})` }} />{r.pretix_label.trim() || 'Pretix'}</span>
            </p>
            <Field label="La référence de l’an dernier se compare à"
              helper="Avant Pretix (avril 2026), la soirée de référence n’a que Shotgun : les soirées d’associations s’y vendaient. Une référence plus récente qui a un événement rattaché compte toute sa nuit, pour comparer nuit à nuit. La tuile Performance suit la même règle.">
              <Select
                value={r.n1_reference}
                onChange={(v) => setR({ ...r, n1_reference: v === 'SHOTGUN_VS_SHOTGUN' ? 'SHOTGUN_VS_SHOTGUN' : 'NIGHT_VS_SHOTGUN' })}
                ariaLabel="La référence de l’an dernier se compare à"
                options={[
                  { value: 'NIGHT_VS_SHOTGUN', label: 'Toute la nuit de cette année, Shotgun et Pretix (conseillé)' },
                  { value: 'SHOTGUN_VS_SHOTGUN', label: 'Shotgun seul : Shotgun contre Shotgun' },
                ]}
              />
            </Field>
            <SwitchRow checked={r.bde_expected} onChange={(v) => setR({ ...r, bde_expected: v })} disabled={!peutGerer}
              title="Afficher les entrées BDE attendues"
              desc="Sous le graphique des entrées : billets Pretix vendus × taux de venue (« au moins 224 »). Les billets se vendent encore pendant la nuit, le chiffre est donc un minimum. La nuit finie, il laisse place au constat : entrées sur billets vendus." />
            {r.bde_expected ? (
              <>
                <Field label="Taux de venue" helper="La part des billets Pretix vendus qui passent la porte : 90 % pour MEDECINE, 91 % pour INTERSANTE, 98 % pour ESN.">
                  <Select
                    value={r.bde_rate_mode}
                    onChange={(v) => setR({ ...r, bde_rate_mode: v === 'FIXED' ? 'FIXED' : 'AUTO' })}
                    ariaLabel="Taux de venue"
                    options={[
                      { value: 'AUTO', label: 'Mesuré sur les soirées d’associations passées (conseillé)' },
                      { value: 'FIXED', label: 'Fixé ici' },
                    ]}
                  />
                </Field>
                {r.bde_rate_mode === 'FIXED' ? (
                  <Field label="Taux fixe">
                    <NumberInput value={r.bde_rate_pct} min={TAUX_MIN} max={TAUX_MAX} unit="%" steppers
                      onChange={(v) => setR({ ...r, bde_rate_pct: Number(v) })} ariaLabel="Taux de venue fixe en pourcentage" />
                  </Field>
                ) : null}
              </>
            ) : null}
          </div>
        </section>

        <section className="lc-panel gd-panel" aria-labelledby="bil-analyses">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="bil-analyses">Analyses</h2></header>
          <div className="lc-panel__body">
            <SwitchRow checked={r.include_in_analytics} onChange={(v) => setR({ ...r, include_in_analytics: v })} disabled={!peutGerer}
              title="Compter les soirées d’associations dans les comparaisons, familles et prévisions"
              desc="Éteint par défaut : une nuit INTERSANTE n’est pas une référence pour la prochaine soirée du club. Allumer reclasse les familles tout de suite." />
            {/* Ce que fait la Comptabilité, lu dans SES réglages quand on peut les lire
                — « elle compte les billets vendus » était faux une fois ce
                réglage éteint (revue du 16/10). */}
            <p className="lc-hint">
              Ce réglage ne touche pas la Comptabilité.{' '}
              {parts === null
                ? <>Elle peut compter les billets Pretix vendus dans les entrées de la nuit, et leur argent, selon ses propres réglages (<Link href="/comptabilite/regles">Comptabilité › Règles</Link>), jamais comme des préventes Shotgun.</>
                : <>
                    {parts.entries_enabled
                      ? 'Elle compte les billets Pretix vendus dans les entrées de la nuit'
                      : 'Elle ne compte pas les billets Pretix dans les entrées de la nuit'}
                    {parts.revenue_enabled
                      ? ', et leur argent (prix moins la part de chaque association) dans le chiffre de la nuit'
                      : ', et pas leur argent'}
                    {' '}: <Link href="/comptabilite/regles">Comptabilité › Règles</Link>. Jamais comme des préventes Shotgun.
                  </>}
            </p>
          </div>
        </section>

        {/* « À rattacher » et « La nuit de chaque événement d'association » ont
            DÉMÉNAGÉ le 20/10/2026 dans Exploitation › Associations › À décider
            (décision de l'exploitant : ce sont des décisions de tous les jours,
            pas des réglages). Ici, il reste le chemin pour y aller — pour qui
            peut l'ouvrir (« Consulter les associations »). Les deux listes
            gardent les droits des SOIRÉES : qui gère les soirées sans lire les
            associations les retrouve donc ICI, sinon il perdait la correction
            de la nuit d'un événement (et le lien le menait à un refus). */}
        <section className="lc-panel gd-panel" aria-labelledby="bil-decisions">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="bil-decisions">À rattacher, et la nuit de chaque événement</h2></header>
          <div className="lc-panel__body">
            {litAssociations ? (
              <>
                <p className="lc-hint" style={{ marginTop: 0 }}>
                  Les événements que Gestion n’a pas pu rattacher seul, et la nuit {peutLireParts ? '(et la part) ' : ''}de chaque événement
                  d’association, se décident maintenant au même endroit que les associations elles-mêmes.
                </p>
                <Link href="/exploitation/associations?vue=a-decider" className="lc-btn">Ouvrir Associations › À décider</Link>
              </>
            ) : (
              <>
                <p className="lc-hint" style={{ marginTop: 0 }}>
                  Ces listes vivent d’habitude dans Exploitation › Associations, que votre rôle n’ouvre pas (« Consulter les
                  associations ») : vous les retrouvez ici, avec vos droits sur les soirées.
                </p>
                <ARattacher complet />
                <h3 className="lc-section-title" style={{ margin: 'var(--sp-6) 0 var(--sp-3)' }}>La nuit {peutLireParts ? '(et la part) ' : ''}de chaque événement d’association</h3>
                <NuitsDesEvenements />
              </>
            )}
          </div>
        </section>
      </main>
    </Shell>
  )
}
